/**
 * PitcrewAI — /business/pitcrew marketing page dedicated to the Garage
 * Management platform ("Pitcrew AI"). Layout mirrors the reference mockup
 * shared by the product owner (Feb 24, 2026). Pricing section intentionally
 * omitted, matching the sibling Propitstop Drive page.
 *
 * Structure:
 *   1. Slim brand nav (logo + Book a Demo + Login)
 *   2. Hero (headline + CTAs + trust stats + dashboard mockup)
 *   3. Positioning strip — "Your garage already has software..."
 *   4. "One Intelligent Flow. End to End." — 12-step horizontal timeline
 *   5. Feature grid — 8 tiles
 *   6. WhatsApp Service Reminders split
 *   7. "From Garage Management System to Garage Operating System" CTA
 *   8. Bottom outcomes strip + footer
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Bot, Wrench, ClipboardList, FileCheck2, Boxes, MessageCircle, BellRing, BarChart3,
  Users, Calendar, Car, ClipboardCheck, FileSpreadsheet, Cog, ShieldCheck, MessagesSquare,
  CreditCard, Star, ArrowRight, ArrowRightCircle, TrendingUp, Gauge, Smile, IndianRupee,
  CheckCircle2, PhoneCall,
} from 'lucide-react';
import PropitstopFooter from '../components/PropitstopFooter';
import SEO from '../components/SEO';
import DemoBookingModal from '../components/DemoBookingModal';
import { useSiteImages } from '../hooks/useSiteImages';

const PITCREW_LOGO =
  'https://customer-assets-eiarnc6j.emergentagent.net/job_fcf8fe40-6235-4ae1-a5a5-ebd4002e0a55/artifacts/vf35w3ym_ChatGPT%20Image%20Aug%208%2C%202026%2C%2007_18_58%20PM.png';

// Dashboard preview image on the hero — a placeholder Unsplash shot until
// we swap in a real Pitcrew AI dashboard screenshot.
const HERO_MOCKUP =
  'https://images.unsplash.com/photo-1581092334438-3c1beba2224e?auto=format&fit=crop&w=1400&q=80';

const HERO_STATS = [
  { icon: Users, value: '500+', label: 'Garages', sub: 'Trust Pitcrew AI' },
  { icon: TrendingUp, value: '20%+', label: 'More Jobs', sub: 'On Average' },
  { icon: Gauge, value: '30%+', label: 'Higher Productivity', sub: 'For Technicians' },
  { icon: ShieldCheck, value: '2X', label: 'Faster Approvals', sub: 'From Customers' },
];

const POSITIONING = [
  {
    icon: Users,
    title: 'Walk-ins → Digital demand',
    body: 'Customers search online before they visit. Pitcrew AI connects the customer journey to your garage.',
  },
  {
    icon: ClipboardList,
    title: 'Job card → Complete workflow',
    body: 'From enquiry, inspection, estimate and approval to repair, quality check and delivery — everything in one flow.',
  },
  {
    icon: BarChart3,
    title: 'Data → Decisions',
    body: 'Turn every job into business intelligence — technicians, parts, revenue, repeat customers and bottlenecks.',
  },
];

const JOURNEY = [
  { icon: Users, label: 'Customer Enquiry' },
  { icon: Calendar, label: 'Booking' },
  { icon: Car, label: 'Vehicle Reception' },
  { icon: ClipboardCheck, label: 'Inspection' },
  { icon: FileSpreadsheet, label: 'Estimate & Approval' },
  { icon: ClipboardList, label: 'Job Card' },
  { icon: Wrench, label: 'Technician Workflow' },
  { icon: Boxes, label: 'Parts & Inventory' },
  { icon: ShieldCheck, label: 'Quality Check' },
  { icon: MessagesSquare, label: 'Customer Updates' },
  { icon: CreditCard, label: 'Payment & Delivery' },
  { icon: Star, label: 'Feedback / Next Service' },
];

const FEATURES = [
  {
    icon: Bot,
    title: 'AI-Powered Job Management',
    desc: 'Create, assign and track jobs intelligently. Prioritize what matters most.',
    accent: 'text-violet-600 bg-violet-50',
  },
  {
    icon: Wrench,
    title: 'Technician Workflow',
    desc: 'Smart task allocation, real-time updates and better productivity.',
    accent: 'text-blue-600 bg-blue-50',
  },
  {
    icon: ClipboardCheck,
    title: 'Digital Inspections & Job Cards',
    desc: 'Standardized checklists, photos, notes and digital job cards.',
    accent: 'text-emerald-600 bg-emerald-50',
  },
  {
    icon: FileCheck2,
    title: 'Estimates & Approvals',
    desc: 'Professional estimates, digital approvals and faster conversions.',
    accent: 'text-orange-600 bg-orange-50',
  },
  {
    icon: Boxes,
    title: 'Parts & Inventory Visibility',
    desc: 'Real-time stock, low stock alerts and smarter reordering.',
    accent: 'text-indigo-600 bg-indigo-50',
  },
  {
    icon: MessageCircle,
    title: 'WhatsApp Customer Communication',
    desc: 'Automated updates, job status, estimates and reminders — on WhatsApp.',
    accent: 'text-green-600 bg-green-50',
  },
  {
    icon: BellRing,
    title: 'Automated Service Reminders',
    desc: 'Never miss a service opportunity. Remind customers on WhatsApp before their next due date.',
    accent: 'text-red-500 bg-red-50',
  },
  {
    icon: BarChart3,
    title: 'Garage Business Intelligence',
    desc: 'Powerful dashboards and reports to help you make smarter business decisions.',
    accent: 'text-cyan-600 bg-cyan-50',
  },
];

const OUTCOMES = [
  { icon: TrendingUp, label: 'More Jobs' },
  { icon: Gauge, label: 'Higher Productivity' },
  { icon: Smile, label: 'Happier Customers' },
  { icon: IndianRupee, label: 'Better Profits' },
];

const OPERATING_BENEFITS = [
  'Increase workshop efficiency',
  'Improve customer satisfaction',
  'Drive repeat business',
  'Grow revenue, sustainably',
];

export default function PitcrewAI() {
  const [demoOpen, setDemoOpen] = useState(false);
  const openDemo = () => setDemoOpen(true);
  const { imageFor } = useSiteImages();
  const heroMockup = imageFor('pitcrew_hero_mockup', HERO_MOCKUP);

  return (
    <div className="min-h-screen bg-white text-zinc-900" data-testid="pitcrew-ai-page">
      <DemoBookingModal
        open={demoOpen}
        onClose={() => setDemoOpen(false)}
        source="pitcrew"
        accent="bg-blue-600 hover:bg-blue-700"
        title="Tell us about your garage"
      />
      <SEO
        title="Pitcrew AI — Stop Depending on Aggregators. Grow Your Garage Business."
        description="Pitcrew AI is the AI operating system for modern garages. Own your customers, run every job end-to-end, drive repeat business — and stop depending on aggregators. Booking, technician workflow, estimates, parts, WhatsApp reminders and reports in one platform."
        canonical={typeof window !== 'undefined' ? `${window.location.origin}/business/pitcrew` : ''}
      />

      {/* ============================== NAV ============================== */}
      <nav data-testid="pitcrew-nav" className="sticky top-0 z-40 bg-white/95 backdrop-blur-xl border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/business/pitcrew" data-testid="pitcrew-nav-logo" className="flex items-center gap-2">
            <img src={PITCREW_LOGO} alt="Pitcrew AI" className="h-10 w-10 rounded-lg object-cover" />
            <div className="leading-tight">
              <div className="font-extrabold text-lg text-zinc-900 tracking-tight">
                pitcrew <span className="text-blue-600">AI</span>
              </div>
              <div className="text-[9px] uppercase tracking-[0.22em] text-zinc-500 font-bold -mt-0.5">Garage Management System</div>
            </div>
          </Link>
          <div className="hidden md:flex items-center gap-7 text-sm font-medium text-zinc-700">
            <a href="#features" className="hover:text-zinc-900">Features</a>
            <a href="#contact" className="hover:text-zinc-900">Contact</a>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={openDemo}
              data-testid="pitcrew-book-demo-nav"
              className="inline-flex items-center gap-2 px-3.5 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold transition-colors"
            >
              <Calendar className="w-4 h-4" /> Book a Demo
            </button>
            <Link
              to="/garages"
              data-testid="pitcrew-login"
              className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-zinc-300 hover:border-zinc-500 text-sm font-medium text-zinc-800"
            >
              Login
            </Link>
          </div>
        </div>
      </nav>

      {/* ============================== HERO ============================= */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-16 grid lg:grid-cols-[1fr,1.35fr] gap-8 lg:gap-14 items-center">
        <div>
          <span className="inline-block px-3 py-1 rounded-full bg-blue-50 text-blue-700 text-[11px] font-bold uppercase tracking-widest">
            AI Operating System for Modern Garages
          </span>
          <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] tracking-tight text-zinc-900">
            Run Your Garage Smarter.<br />
            <span className="text-blue-600">From Job Booking to</span><br />
            <span className="text-blue-600">Customer Delivery.</span>
          </h1>
          <p className="mt-5 text-base sm:text-lg text-zinc-500 max-w-xl leading-relaxed">
            Pitcrew AI brings every part of your garage operations, technicians, customers and business insights together in one intelligent operating layer.
          </p>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={openDemo}
              data-testid="pitcrew-book-demo-hero"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold transition-colors shadow-lg shadow-blue-500/25"
            >
              <Calendar className="w-4 h-4" /> Book a Demo <ArrowRight className="w-4 h-4" />
            </button>
          </div>

          <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-4">
            {HERO_STATS.map((s) => (
              <div key={s.label} className="flex items-start gap-2">
                <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                  <s.icon className="w-4 h-4 text-blue-600" />
                </div>
                <div>
                  <div className="text-lg font-extrabold text-zinc-900 leading-none">{s.value}</div>
                  <div className="text-[11px] font-semibold text-zinc-800 leading-tight mt-1">{s.label}</div>
                  <div className="text-[10px] text-zinc-500 leading-tight">{s.sub}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Hero mockup — larger, cinematic frame with soft halo (floating stat cards removed per product decision) */}
        <div className="relative">
          <div className="absolute -inset-3 sm:-inset-4 bg-gradient-to-br from-blue-100 via-white to-cyan-100 rounded-[36px] blur-xl opacity-70" />
          <div className="relative rounded-[28px] bg-gradient-to-br from-blue-50 via-white to-cyan-50 p-2 sm:p-3 border border-white/70 shadow-2xl shadow-blue-200/50">
            <img
              src={heroMockup}
              alt="Pitcrew AI dashboard preview"
              fetchPriority="high"
              decoding="async"
              className="w-full aspect-[16/10] object-contain bg-white rounded-[22px]"
              data-testid="pitcrew-hero-image"
            />
          </div>
        </div>
      </section>

      {/* ======================== POSITIONING STRIP ====================== */}
      <section className="bg-zinc-50 border-y border-zinc-200 py-14">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-zinc-900">
            Your garage already has software.
          </h2>
          <p className="mt-1 text-xl sm:text-2xl lg:text-3xl font-extrabold tracking-tight text-blue-600">
            But does it actually run the business?
          </p>
          <div className="mt-10 grid md:grid-cols-3 gap-6 text-left">
            {POSITIONING.map((p) => (
              <div key={p.title} className="p-5 bg-white border border-zinc-200 rounded-2xl">
                <div className="w-11 h-11 rounded-xl bg-blue-50 flex items-center justify-center">
                  <p.icon className="w-5 h-5 text-blue-600" />
                </div>
                <h3 className="mt-4 text-base font-extrabold text-zinc-900">{p.title}</h3>
                <p className="mt-1.5 text-sm text-zinc-500 leading-relaxed">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Journey timeline removed per product decision (Feb 24, 2026) —
          flow ordering was incorrect. Keeping this comment as a breadcrumb
          in case we want to bring back a cleaner version. */}

      {/* ============================ FEATURES ============================ */}
      <section id="features" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20">
        <div className="text-center max-w-3xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-zinc-900">
            Everything your garage needs to <span className="text-blue-600">run, retain &amp; grow</span>
          </h2>
        </div>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {FEATURES.map((f) => (
            <div
              key={f.title}
              data-testid={`pitcrew-feature-${f.title.toLowerCase().replace(/\s+/g, '-').replace(/&/g, 'and')}`}
              className="p-5 rounded-2xl bg-white border border-zinc-200 hover:border-blue-300 hover:shadow-lg transition-all"
            >
              <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${f.accent}`}>
                <f.icon className="w-5 h-5" />
              </div>
              <h3 className="mt-4 text-base font-extrabold text-zinc-900">{f.title}</h3>
              <p className="mt-1.5 text-sm text-zinc-500 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ================== WHATSAPP SERVICE REMINDERS =================== */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-14 lg:pb-20">
        <div className="grid lg:grid-cols-2 gap-8 items-center bg-emerald-50/40 border border-emerald-100 rounded-3xl p-6 lg:p-10">
          <div>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-emerald-500 flex items-center justify-center">
                <MessageCircle className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-2xl lg:text-3xl font-extrabold text-zinc-900">WhatsApp Service Reminders</h3>
            </div>
            <p className="mt-3 text-base font-semibold text-zinc-700">Bring customers back. Automatically.</p>
            <p className="mt-2 text-sm text-zinc-500 leading-relaxed max-w-md">
              Pitcrew AI helps you stay connected with your customers and bring them back for their next service.
            </p>
            <ul className="mt-5 space-y-2.5 text-sm text-zinc-700">
              {[
                'Next service due date captured automatically',
                'Personalized WhatsApp reminder sent on time',
                'One tap booking link to drive repeat business',
              ].map((t) => (
                <li key={t} className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white rounded-2xl p-4 border border-zinc-200 text-center">
              <div className="w-10 h-10 mx-auto rounded-full bg-emerald-100 flex items-center justify-center">
                <BellRing className="w-5 h-5 text-emerald-600" />
              </div>
              <div className="mt-3 text-xs font-bold text-emerald-700">REMINDER SENT</div>
              <div className="mt-1 text-xs text-zinc-500">Next Service due in</div>
              <div className="mt-1 text-2xl font-extrabold text-zinc-900">15 Days</div>
              <div className="mt-2 text-[10px] text-zinc-500">Maruti Swift · PB 10 AB 1234</div>
            </div>
            <div className="bg-white rounded-2xl p-4 border border-zinc-200">
              <div className="text-[10px] font-bold text-emerald-700">Demo Garage · Online</div>
              <div className="mt-2 text-xs text-zinc-800 leading-snug">
                Hello Raj, Your Maruti Swift (PB 10 AB 1234) is due for its next service on 05 May 2026. Keep your car in the best condition. Book your service with us today.
              </div>
              <div className="mt-3 inline-block px-2 py-1 rounded-lg bg-emerald-500 text-white text-[10px] font-bold">
                Book Service Now
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================== FROM GMS TO GARAGE OS + CTA ================== */}
      <section id="contact" className="bg-gradient-to-r from-blue-50 via-blue-50/50 to-white border-t border-blue-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16 grid lg:grid-cols-[1.15fr,1fr] gap-8 items-center">
          <div>
            <h3 className="text-2xl lg:text-3xl font-extrabold text-zinc-900">
              From a Garage Management System<br />
              to a <span className="text-blue-600">Garage Operating System.</span>
            </h3>
            <p className="mt-3 text-sm text-zinc-600 max-w-xl">
              See Pitcrew AI in action and experience how a modern garage can run from one intelligent operating layer.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={openDemo}
                data-testid="pitcrew-cta-book"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold transition-colors shadow-lg shadow-blue-500/25"
              >
                <Calendar className="w-4 h-4" /> Book a Demo <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-2.5">
            {OPERATING_BENEFITS.map((b) => (
              <div key={b} className="flex items-center gap-2.5 bg-white rounded-xl px-4 py-3 border border-zinc-200">
                <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                <span className="text-sm font-semibold text-zinc-800">{b}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Outcome strip */}
        <div className="border-t border-blue-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 grid grid-cols-2 md:grid-cols-4 gap-4">
            {OUTCOMES.map((o) => (
              <div key={o.label} className="flex items-center justify-center gap-2 text-zinc-700">
                <o.icon className="w-4 h-4 text-blue-600" />
                <span className="text-sm font-semibold">{o.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <PropitstopFooter />
    </div>
  );
}
